import { Lightbox, Stack, Text, Upload } from "@assetwatch/ui";
import { useState } from "react";
import type { ProcedureResponsePhoto } from "../../queries/useProcedureExecution";
import {
  ProcedurePhotoTile,
  type ProcedurePhotoTileStatus,
} from "../snowflakes/work-orders/ProcedurePhotoTile";
import {
  IMAGE_ACCEPT,
  MAX_PHOTOS_PER_ITEM,
  type PhotoUploadEntry,
} from "./procedurePhotoUpload";

function entryStatus(entry: PhotoUploadEntry): ProcedurePhotoTileStatus {
  if (entry.phase === "saving") return { kind: "saving" };
  if (entry.phase !== "failed") return { kind: "uploading" };
  const retry = entry.error?.retry ?? "none";
  return {
    kind: "failed",
    message: entry.error?.message ?? "Unable to upload the photo.",
    retryLabel:
      retry === "new-session"
        ? "Upload again"
        : retry === "save"
          ? "Retry save"
          : retry === "none"
            ? null
            : "Retry upload",
  };
}

/**
 * The response control for a PHOTO item: saved photos (opening in a
 * lightbox), photos still uploading or failed, and the picker while the item
 * has room. When `canChange` is false only the saved thumbnails are shown.
 */
export function ProcedurePhotoField({
  canChange,
  entries,
  itemLabel,
  error = false,
  errorDescriptionId,
  onDiscard,
  onFilesSelected,
  onImageError,
  onRemoveSaved,
  onRetry,
  removingIds,
  savedFileIds,
  savedPhotos,
  selectionError,
}: {
  canChange: boolean;
  entries: PhotoUploadEntry[];
  error?: boolean;
  errorDescriptionId?: string;
  itemLabel: string;
  onDiscard: (localId: string) => void;
  onFilesSelected: (files: File[]) => void;
  onImageError?: () => void;
  onRemoveSaved: (fileId: string) => void;
  onRetry: (localId: string) => void;
  removingIds: string[];
  savedFileIds: string[];
  savedPhotos: ProcedureResponsePhoto[];
  selectionError: string | null;
}) {
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const savedIds = new Set(savedFileIds);
  // An entry whose photo is already saved renders from the server copy.
  const pending = canChange
    ? entries.filter((entry) => !savedIds.has(entry.uploadId))
    : [];
  const slotsUsed = savedFileIds.length + pending.length;
  const hasTiles = savedPhotos.length > 0 || pending.length > 0;

  return (
    <Stack gap={12}>
      {hasTiles ? (
        /* @ds-deviation: the POC shows one "Photo attached" chip per item; the acceptance criteria need several photos with per-photo pending, saved and failed states, so a wrapping row of Thumbnail tiles replaces the chip — promote a multi-photo evidence field into the design system */
        <ul
          aria-label={`${itemLabel} photos`}
          className="m-0 flex list-none flex-wrap gap-(--spacing-12) p-0"
          onErrorCapture={onImageError}
        >
          {savedPhotos.map((photo, index) => (
            <li key={photo.fileId}>
              <ProcedurePhotoTile
                alt={photo.fileName}
                fileName={photo.fileName}
                onOpen={() => setViewerIndex(index)}
                onRemove={
                  canChange ? () => onRemoveSaved(photo.fileId) : undefined
                }
                src={photo.downloadUrl}
                status={
                  removingIds.includes(photo.fileId)
                    ? { kind: "removing" }
                    : { kind: "saved" }
                }
              />
            </li>
          ))}
          {pending.map((entry) => (
            <li key={entry.localId}>
              <ProcedurePhotoTile
                alt={entry.file.name}
                fileName={entry.file.name}
                onRemove={
                  entry.phase === "saving"
                    ? undefined
                    : () => onDiscard(entry.localId)
                }
                onRetry={() => onRetry(entry.localId)}
                src={entry.previewUrl}
                status={entryStatus(entry)}
              />
            </li>
          ))}
        </ul>
      ) : null}
      {canChange && slotsUsed < MAX_PHOTOS_PER_ITEM ? (
        /* @ds-deviation: the POC's compact dashed "Attach photo or file" chip with a paperclip icon has no DS equivalent and the DS icon set has no paperclip, so the DS Upload (photo variant, larger fixed frame) is used — fix in product if the POC chip is not required, otherwise promote a compact Upload variant into the design system */
        <Upload
          accept={IMAGE_ACCEPT}
          acceptedFormatsLabel="JPEG, PNG, WebP"
          aria-label={`${itemLabel} photo upload`}
          aria-describedby={errorDescriptionId}
          buttonLabel="Attach photo or file"
          error={error || undefined}
          maxSizeLabel="25 MB"
          multiple
          onFilesSelected={onFilesSelected}
        />
      ) : null}
      {!canChange && savedPhotos.length === 0 ? (
        <Text size="sm" tone="subtle">
          No photos
        </Text>
      ) : null}
      {selectionError ? (
        <Text role="alert" size="sm" tone="negative">
          {selectionError}
        </Text>
      ) : null}
      <Lightbox
        aria-label={`${itemLabel} photos`}
        images={savedPhotos.map((photo) => ({
          alt: photo.fileName,
          src: photo.downloadUrl,
        }))}
        index={viewerIndex ?? 0}
        onIndexChange={setViewerIndex}
        onOpenChange={(open) => {
          if (!open) setViewerIndex(null);
        }}
        open={viewerIndex !== null}
      />
    </Stack>
  );
}
